import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SvgShowMore } from "@routes/IconeSvg";
import Button from "@components/tailwindComponents/Button";
import ListboxEmploye from "@components/tailwindComponents/ListboxEmploye";
import ListboxGroupe from "@components/tailwindComponents/ListboxGroupe/index";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import ListboxHierarchical from "@components/tailwindComponents/ListboxHierarchical";
import { useNotification } from "@hooks/useNotification";
import { ACCESS_BATCH, BATCH_ACCESS_PERMISSIONS, JOB_SALARY_HOLIDAYS, PERSONAL_INFO } from "../container/bulkEdit/utils/bulkEditHelpers";
import { SECTION_BULK_CONFIG } from "@helpers/_constants";
import { useReduxSelector } from "@hooks/useReduxSelector";
import Loupe from "@assets/image/icones/employer/loup.svg";
import { InputLoupe } from "@components/input";

function EmployeTabFilter(props) {
  const { 
    defaultValues = {}, 
    isLoading = false, 
    withSeeMore = false, 
    employeeBulkFileAvailableSections = [], 
    additionalClassParent = "",
    onSearchChange,
    withSearch = false,
  } = props;
  const [selectedEmployes, setSelectedEmployes] = useState([]);
  const [selectedEmplacements, setSelectedEmplacements] = useState([]);
  const [selectedGroupes, setSelectedGroupes] = useState([]);
  const [selectedPostes, setSelectedPostes] = useState([]);
  const [selectedPersonalInfo, setSelectedPersonalInfo] = useState(null);
  const [selectedJobSalaryHolidays, setSelectedJobSalaryHolidays] = useState(null);
  const [showFilter, setShowFilter] = useState(props.defaultOpen || false);
  const [searchTerm, setSearchTerm] = useState("");
  const { employers, inactiveEmployees, emplacements, groupes, postes } = useSelector((state) => state.commonData);
  const accessList = useSelector((state) => state.userConnected.accessList || []);
  const { t } = useTranslation("pages.employés.listEmployés");
  const { showWarning } = useNotification();
  const useAdditionalInfo = useReduxSelector(({ settings }) => settings?.enterprise?.useAdditionalInfo) || false;
  const [selectedSuperiorHierarchique, setSelectedSuperiorHierarchique] = useState(null);

  const filteredPersonalInfo = useMemo(() => {
    return PERSONAL_INFO.filter(item => {
      if (item._id === SECTION_BULK_CONFIG.additionalInformation._id && !useAdditionalInfo) return false;
      const sectionConfig = Object.values(SECTION_BULK_CONFIG).find(config => config._id === item._id);
      return sectionConfig && employeeBulkFileAvailableSections.some(section => section.includes(sectionConfig.permission));
    });
  }, [employeeBulkFileAvailableSections]);

  const filteredJobSalaryHolidays = useMemo(() => {
    return JOB_SALARY_HOLIDAYS.filter(item => {
      const sectionConfig = Object.values(SECTION_BULK_CONFIG).find(config => config._id === item._id);
      return sectionConfig && employeeBulkFileAvailableSections.some(section => section.includes(sectionConfig.permission));
    });
  }, [employeeBulkFileAvailableSections]);

  const checkBatchPermission = (sectionKey) => {
    const permissionId = BATCH_ACCESS_PERMISSIONS[sectionKey];
    if (!permissionId) return true;

    if (accessList.includes(permissionId)) {
      const permissionData = ACCESS_BATCH.find(item => item._id === permissionId);
      showWarning("multiple_modification_unavailable", {
        object: t(permissionData.name).toLowerCase()
      });
      return false;
    }
    return true;
  };

  const handlePersonalInfoChange = (value) => {
    if (value && !checkBatchPermission(value._id)) return;
    if (value) setSelectedJobSalaryHolidays(null);
    setSelectedPersonalInfo(value);
  };

  const handleJobSalaryHolidaysChange = (value) => {
    if (value && !checkBatchPermission(value._id)) return;
    if (value) setSelectedPersonalInfo(null);
    setSelectedJobSalaryHolidays(value);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  useEffect(() => {
    let defaultEmployes = [];
    if (defaultValues?.employes?.length && employers.length) {
      defaultEmployes = employers.filter((employer) =>
        defaultValues.employes.includes(employer.id)
      );
    }
    setSelectedEmployes(defaultEmployes);
  }, [employers, defaultValues?.employes]);

  useEffect(() => {
    if (defaultValues?.emplacements?.length && emplacements.length) {
      const defaultEmplacements = emplacements.filter((emplacement) =>
        defaultValues.emplacements.includes(emplacement._id)
      );
      setSelectedEmplacements(defaultEmplacements);
    }
  }, [emplacements, defaultValues?.emplacements]);

  useEffect(() => {
    if (defaultValues?.groupes?.length && groupes?.length) {
      setSelectedGroupes(defaultValues?.groupes);
    }
  }, [groupes, defaultValues?.groupes]);

  useEffect(() => {
    if (defaultValues?.personalInfo?.length) {
      const selected = PERSONAL_INFO.find(item => item._id === defaultValues.personalInfo[0]);
      setSelectedPersonalInfo(selected || null);
    }
  }, [defaultValues?.personalInfo]);

  useEffect(() => {
    if (defaultValues?.jobSalaryHolidays?.length) {
      const selected = JOB_SALARY_HOLIDAYS.find(item => item._id === defaultValues.jobSalaryHolidays[0]);
      setSelectedJobSalaryHolidays(selected || null);
    }
  }, [defaultValues?.jobSalaryHolidays]);

  useEffect(() => {
    if (typeof defaultValues?.searchTerm === "string") {
      setSearchTerm(defaultValues.searchTerm || "");
    }
  }, [defaultValues?.searchTerm]);

  function onApplyClicked() {
    const filterData = {
      employes: selectedEmployes.map((e) => e.id),
      emplacements: selectedEmplacements.map((e) => e._id),
      postes: selectedPostes.map((e) => e._id),
      groupes: selectedGroupes,
      personalInfo: selectedPersonalInfo ? [selectedPersonalInfo._id] : [],
      jobSalaryHolidays: selectedJobSalaryHolidays ? [selectedJobSalaryHolidays._id] : [],
      superiorHierarchique: selectedSuperiorHierarchique?.id || null,
      searchTerm: searchTerm
    };
    props.onFilter(filterData);
  }

  const fieldGridClass = useMemo(() => {
    const hasHierarchical = Boolean(props.withHierarchical);
    if (props.withPostes) {
      return withSearch
        ? hasHierarchical ? "arh-grid-cols-7" : "arh-grid-cols-6"
        : hasHierarchical ? "arh-grid-cols-6" : "arh-grid-cols-5";
    }
    return withSearch
      ? hasHierarchical ? "arh-grid-cols-6" : "arh-grid-cols-5"
      : hasHierarchical ? "arh-grid-cols-5" : "arh-grid-cols-4";
  }, [props.withPostes, props.withHierarchical, withSearch]);

  return (
    <div className={`arh-w-full arh-rounded arh-bg-greyColor arh-p-4 ${additionalClassParent}`}>
      <div className={`arh-grid arh-gap-4 ${fieldGridClass}`}>
        {withSearch && (
          <InputLoupe
            text={t("Nom, prénom, numéro d'employé")}
            name="name"
            src={Loupe}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onApplyClicked();
              }
            }}
          />
        )}
        <ListboxEmploye
          placeholder={t("Employé")}
          name="employe"
          optionList={(props.showInactive ? inactiveEmployees : employers) || []}
          selectedValue={selectedEmployes}
          setSelectedValue={setSelectedEmployes}
          customClass="arh-ml-[0.925rem]"
          multiple
          showReset
        />
        <ListboxSimple
          placeholder={t("Emplacement")}
          name="emplacement"
          optionList={emplacements || []}
          selectedValue={selectedEmplacements}
          setSelectedValue={setSelectedEmplacements}
          withSearch
          multiple
          showReset
        />
        <ListboxGroupe
          placeholder={t("Groupes")}
          name="Groupes"
          optionList={groupes}
          selectedValue={selectedGroupes}
          setSelectedValue={setSelectedGroupes}
          withSearch
          showReset
        />
        {props.withHierarchical && (
          <ListboxHierarchical
            placeholder={t("Supérieur hiérarchique")}
            name="superiorHierarchique"
            optionList={employers || []}
            selectedValue={selectedSuperiorHierarchique}
            setSelectedValue={setSelectedSuperiorHierarchique}
            showReset={Boolean(selectedSuperiorHierarchique)}
          />
        )}
        {props.withPostes && (
          <ListboxSimple
            placeholder={t("Poste")}
            name="poste"
            optionList={postes || []}
            optionTextAttribut="title"
            selectedValue={selectedPostes}
            setSelectedValue={setSelectedPostes}
            withSearch
            withTooltip
            multiple
            showReset
          />
        )}
        <div className="arh-flex arh-items-center arh-gap-4">
          <Button wait={isLoading} onClick={onApplyClicked} disabled={props.disabled}>
            {t("Appliquer")}
          </Button>
          {withSeeMore && (
            <Button
              variant={`${props.disabled ? "outlined" : "green-outlined"}`}
              onClick={toggleFilter}
              className="!arh-w-[2.625rem]"
              disabled={props.disabled}
            >
              <SvgShowMore color={props.disabled ? "#8A8A8A" : undefined} />
            </Button>
          )}
        </div>
      </div>
      <div className={`${showFilter ? "arh-block arh-mt-4" : "arh-hidden"}`}>
        <div className="arh-grid arh-grid-cols-6 arh-gap-4">
          {employeeBulkFileAvailableSections.some(section => section.includes("DListInfopers")) && (
            <ListboxSimple
              placeholder={t("Informations Personnelles")}
              name="personalInfo"
              optionList={filteredPersonalInfo}
              selectedValue={selectedPersonalInfo}
              setSelectedValue={handlePersonalInfoChange}
              noRadio
              withTooltip
              showReset={!!selectedPersonalInfo}
              customTranslator={t}
            />
          )}
          {employeeBulkFileAvailableSections.some(section => section.includes("DListEmploisalary")) && (
            <ListboxSimple
              placeholder={t("Emploi, salaires et vacances")}
              name="jobSalaryHolidays"
              optionList={filteredJobSalaryHolidays}
              selectedValue={selectedJobSalaryHolidays}
              setSelectedValue={handleJobSalaryHolidaysChange}
              noRadio
              withTooltip
              showReset={!!selectedJobSalaryHolidays}
              customTranslator={t}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(EmployeTabFilter);
